import { MediaOper, MediaType, RecvState, EventType, PubEventType } from "../../common/enums"
import protocolSender from "../util/protocol-sender"
import { promiseHandler, hstRequest } from "../../common/promise-handler"
import { getEventPostfix, tracelog, getBitRate } from "../../common/common"
import mediaManager from "./media-manager"
import eventRegistry from "../../common/event-registry"
import store from "../util/store"
import logger from "../../common/logger"

class ShareManager {
  constructor() {
    // Screen share media stream
    this.screenShareStream = null;
    this.screenShareStreamRefCount = 0;

    // Make sure one screen share stream
    this.isCreatingStream = false;

    // User defined screen share parameters(init value is default)
    this.screenShareParam = { frameRate: 15, width: 1920, height: 1080, bitRate: 0 };

    // 用来控制是否释放屏幕共享流
    this.isPublishing = false;
  }

  /**
   * 开始屏幕共享
   * @param {*} shareParam { width: 640, height: 360, frameRate: 15, bitRate: 1000 }
   */
  @tracelog
  startScreenShare(shareParam) {
    if (shareParam) { 
      if (!('width' in shareParam) || typeof shareParam.width !== 'number') {
        logger.error("No width or invalid width in shareParam!");
        return;
      }

      if (!('height' in shareParam) || typeof shareParam.height !== 'number') {
        logger.error("No height or invalid height in shareParam!");
        return;
      }
      
      if (!('frameRate' in shareParam) || typeof shareParam.frameRate !== 'number') {
        logger.error("No frameRate or invaid frameRate in shareParam!");
        return;
      }
      
      if (!('bitRate' in shareParam) || typeof shareParam.bitRate !== 'number') {
        logger.error("No bitRate or invalid bitRate in shareParam!");
        return;
      }

      // 保存参数
      this.setScreenShareParam(shareParam);

      logger.log("Set screen share param: ", shareParam);
    }

    protocolSender.sendPublishMediaReq(
      MediaOper.START_PUBLISH, 
      MediaType.SCREEN_SHARE, 
      "0");

    this.isPublishing = true;
  };

  /**
   * 停止屏幕共享
   */
  stopScreenShare() {
    protocolSender.sendPublishMediaReq(
      MediaOper.STOP_PUBLISH, 
      MediaType.SCREEN_SHARE, 
      "0");

    this.isPublishing = false;
    this.releaseScreenShareStream();
  };

  /**
   * 开始接受远端屏幕共享
   * @param {*} userId 
   * @param {*} mediaId 
   */
  startReceiveScreenShare(userId, mediaId) {
    return hstRequest.request({
      eventName: EventType.EVENT_START_RCV_MEDIA + getEventPostfix(userId, MediaType.SCREEN_SHARE, mediaId),
      retryConfig: {
        num: 0,
        max: 4
      },
      requestFn: () => {
        protocolSender.sendRecvMediaReq(
          RecvState.START_RECV, 
          userId, 
          MediaType.SCREEN_SHARE, 
          mediaId);
      }
    })
  };

  /**
   * 停止接收远端屏幕共享
   * @param {*} userId 
   * @param {*} mediaId 
   */
  stopReceiveScreenShare(userId, mediaId) {
    protocolSender.sendRecvMediaReq(
      RecvState.STOP_RECV, 
      userId, 
      MediaType.SCREEN_SHARE, 
      mediaId);

    return promiseHandler(EventType.EVENT_STOP_RCV_MEDIA + getEventPostfix(userId, MediaType.SCREEN_SHARE, mediaId));
  };

  /**
   * 设置本地屏幕共享渲染器
   * @param {*} videoEle 
   */
  setLocalScreenShareRender(videoEle) {
    if (!videoEle || !('srcObject' in videoEle)){
      logger.error("Invalid videoEle!");
      return;
    }
    // fix bug #2391
    (async(handle) => {
      let render_stream = null;
      let pc = mediaManager.findUpPcByMedia(MediaType.SCREEN_SHARE, "0");
      if (!pc){ // 还没有广播
        let stream = await this.getScreenShareStream();
        if (stream){
          render_stream = stream;
        } else {
          eventRegistry.trigger(PubEventType.PUB_EVENT_GET_STREAM_FAILED, {
            type: MediaType.SCREEN_SHARE,
            reason: 'GET_STREAM_FAILED'
          })
           console.error("Get screen share stream failed!");
         }
       } else { // 已经在广播
         render_stream = pc.mediaStream;
       }
       if (render_stream){
         handle.srcObject = render_stream;
       } else {
         console.error("Cannot get screen share media stream!");
       }
     })(videoEle);
  }

  /**
   * 取消设置渲染器
   * @param {*} videoEle 
   */
  unsetScreenShareRender(userId, videoEle) {
    if (!videoEle || !('srcObject' in videoEle)){
      logger.error("Invalid videoEle!");
      return;
    }

    videoEle.srcObject = null;

    if (userId == store.userInfo.userId) {
      this.releaseScreenShareStream();
    }
  }

  /**
   * 监听webrtc流
   * @param {*} stream 
   * @param {*} callback 
   */
  _addStreamStopListener(stream, callback) {
    stream.addEventListener('ended', function() {
      callback();
      callback = function() {};
    }, false);
    stream.addEventListener('inactive', function() {
      callback();
      callback = function() {};
    }, false);
    stream.getTracks().forEach(function(track) {
      track.addEventListener('ended', function() {
        callback();
        callback = function() {};
      }, false);
      track.addEventListener('inactive', function() {
        callback();
        callback = function() {};
      }, false);
    });
  }

  /**
   * 监听屏幕共享流被关闭后，通知上层应用
   * @param {*} stream 
   */
  _addScreenShareStreamListener(stream) {
    let self = this;
    this._addStreamStopListener(stream, function() {
      if (self.screenShareStream) {
        eventRegistry.trigger(PubEventType.PUB_EVENT_SCREEN_SHARE_STOPPED);
      }
    });
  }

  /**
   * 获取屏幕共享流，由于采集屏幕共享流会有弹框，因此，
   * 本地预览和发到服务器使用同一条流，和视频不一样
   */
  async getScreenShareStream() {
    // 屏幕共享流存在，直接返回
    if (this.screenShareStream){
      this.screenShareStreamRefCount++;
      return this.screenShareStream;
    } 

    // 正在创建屏幕共享流，则等待直到创建成功
    if (this.isCreatingStream) {
      await this._waitForStreamCreated();
      this.screenShareStreamRefCount++;
      return this.screenShareStream;
    } 
    
    // 还没有创建屏幕共享流，则开始创建
    this.isCreatingStream = true;
    try {
      let constraints = mediaManager.constructConstraints({
        mediaType: MediaType.SCREEN_SHARE,
        mediaId: "screen-share"
      });

      let stream = null;
      if (navigator.mediaDevices.getDisplayMedia) {
        stream = await navigator.mediaDevices.getDisplayMedia(constraints);
      } else if (navigator.getDisplayMedia) { // 兼容Edge
        stream = await navigator.getDisplayMedia(constraints);
      } else {
        throw "Cannot find interface to get screen-share stream!";
      }

      this._addScreenShareStreamListener(stream);

      this.screenShareStreamRefCount++;
      this.screenShareStream = stream;
      this.isCreatingStream = false;
      return stream;
    } catch(err) {
      console.log(err);
      this.isCreatingStream = false;
      return null;
    }
  }

  /**
   * 同步等待屏幕共享流创建完成
   */
  async _waitForStreamCreated(){
    logger.log("Waiting for stream created!");
    while (this.isCreatingStream){
      await new Promise(resolve => {
        setTimeout(resolve, 100);
      });
    }
  }

  /**
   * 销毁屏幕共享流
   */
  destroyScreenShareStream() {
    if (this.screenShareStream) {
      for (const track of this.screenShareStream.getTracks()){
        track.stop();
        logger.log("Stop screen share media stream track.");
      }
      this.screenShareStream = null;
    }
    this.isCreatingStream = false;
    this.screenShareStreamRefCount = 0;
  }

  /**
   * 根据引用计数释放屏幕共享流
   */
  @tracelog
  releaseScreenShareStream() {
    if (!this.isPublishing) {
      this.screenShareStreamRefCount--;
      if (this.screenShareStreamRefCount <= 0) {
        this.destroyScreenShareStream();
      }
    } else {
      logger.warn("Is publishing while try to release screen share stream.");
    }
  }

  /**
   * 获取屏幕共享码流
   */
  getScreenShareBitRate() {
    if (this.screenShareParam.bitRate == 0) {
      return getBitRate(this.screenShareParam.width, this.screenShareParam.height, this.screenShareParam.frameRate);
    } else {
      return this.screenShareParam.bitRate;
    }
  }

  /**
   * 设置屏幕共享参数
   * @param {*} params 
   */
  setScreenShareParam(params) {
    this.screenShareParam = params;
  }

  /**
   * 构造屏幕共享流采集constraints
   */
  buildConstraints() {
    let shareConstraints = { };
    shareConstraints.frameRate = { ideal: this.screenShareParam.frameRate };
    shareConstraints.width = { ideal: this.screenShareParam.width };
    shareConstraints.height = { ideal: this.screenShareParam.height };
    return { video: shareConstraints };
  }
}

export default new ShareManager();